import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "../styles/pages/UnassignedMails.module.css";

import SearchInput from "../ui/inputs/SearchInput";
import ChoiceChip from "../ui/selectors/ChoiceChip";
import FloatingModal from "../ui/Modals/FloatingModal";
import { ConfirmDeleteModal } from "../components/admin/AdminDetailModals";
import LeadInfoModal from "../components/leads/LeadInfo";
import UnassignedMailList from "../components/unassignedMails/UnassignedMailList";
import UnassignedMailDetail from "../components/unassignedMails/UnassignedMailDetail";
import UnassignedMailAssignPanel from "../components/unassignedMails/UnassignedMailAssignPanel";
import { getSenderDisplayName } from "../components/unassignedMails/unassignedMailPills";
import { useLeadContext } from "../contexts/LeadContext";
import { mailsService } from "../services/mails.service";
import { leadsService } from "../services/leads.service";
import { describeRequestError } from "../utils/requestErrorMessage";
import { emitUnassignedPendingCount } from "../utils/unassignedMailsEvents";

const PAGE_SIZE = 50;
const TOAST_DURATION_MS = 7000;
const LOAD_MORE_THRESHOLD_PX = 300;
const MAIL_GONE = "el correo ya no está en Sin asignar";

const ACTION_FAILURES = {
  ignore: "No se pudo ignorar el correo",
  restore: "No se pudo volver el correo a Pendientes",
  spam: "No se pudo marcar el correo como spam",
  delete: "No se pudo eliminar el correo",
  assign: "No se pudo vincular el correo al lead",
};

const TABS = [
  { value: "pending", label: "Pendientes" },
  { value: "ignored", label: "Ignorados" },
  { value: "assigned", label: "Asignados" },
];

const joinNames = (names) =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} y ${names[names.length - 1]}`;

const senderDomainName = (mail) => {
  const domain = String(mail?.sender_domain ?? "").trim().toLowerCase();
  if (!domain) return "";
  const label = domain.split(".")[0] || "";
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : "";
};

const getMailCorpAccountId = (mailCorp) =>
  typeof mailCorp?.account === "number" ? mailCorp.account : mailCorp?.account?.id ?? null;

const personFromMailEntry = (entry) => ({
  id: entry.person.id,
  name: entry.person.name || entry.name || entry.email,
  position: entry.person.position || "",
  email: entry.email,
  company_id: entry.person.company_id ?? null,
  company_name: entry.person.company_name || "",
});

export const buildLeadPrefill = (mail, { responsibleOptions = [], accounts = [] } = {}) => {
  const knownEntries = (mail?.persons ?? []).filter((entry) => entry.person);
  const senderEntry =
    knownEntries.find((entry) => entry.role === "remitente") ?? knownEntries[0] ?? null;
  const receptor = senderEntry ? personFromMailEntry(senderEntry) : null;
  const relatedPersons = knownEntries
    .filter((entry) => entry !== senderEntry)
    .map(personFromMailEntry);

  const company =
    receptor?.company_id != null
      ? { id: receptor.company_id, name: receptor.company_name }
      : null;

  const responsible =
    responsibleOptions.find(
      (option) => Number(option.id) === Number(mail?.mail_account?.id),
    ) ?? null;
  const accountId = getMailCorpAccountId(responsible);
  const account =
    accountId != null
      ? (accounts.find((option) => Number(option.id) === Number(accountId)) ?? null)
      : null;

  return {
    lead_name:
      receptor?.company_name ||
      senderDomainName(mail) ||
      mail?.from_name?.trim() ||
      "",
    person_ref: receptor,
    related_persons: relatedPersons,
    company,
    responsible,
    account,
  };
};

const UnassignedMailsPage = () => {
  const navigate = useNavigate();
  const { statuses, responsible, accounts } = useLeadContext();

  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [mails, setMails] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState(null);
  const listQueryRef = useRef(0);
  const nextOffsetRef = useRef(null);
  const appendingRef = useRef(false);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const detailQueryRef = useRef(0);

  const [leads, setLeads] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState(null);

  const [selectedLead, setSelectedLead] = useState(null);
  const [actionPending, setActionPending] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [createLeadPrefill, setCreateLeadPrefill] = useState(null);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const listContextRef = useRef(`${tab}|${search}`);
  const mailsRef = useRef(mails);
  const selectedIdRef = useRef(selectedId);

  useEffect(() => {
    listContextRef.current = `${tab}|${search}`;
    mailsRef.current = mails;
    selectedIdRef.current = selectedId;
  }, [tab, search, mails, selectedId]);

  const activeStatuses = useMemo(
    () =>
      (statuses ?? [])
        .filter((status) => status.is_active)
        .sort((left, right) =>
          left.order !== right.order ? left.order - right.order : left.id - right.id,
        ),
    [statuses],
  );

  const publishPendingCount = useCallback((count) => {
    setPendingCount(count);
    emitUnassignedPendingCount(count);
  }, []);

  const loadMails = useCallback(
    async ({ append = false } = {}) => {
      const generation = ++listQueryRef.current;
      const offset = append ? (nextOffsetRef.current ?? 0) : 0;

      if (append) {
        appendingRef.current = true;
        setLoadingMore(true);
      } else {
        setListLoading(true);
        setListError(null);
      }

      try {
        const response = await mailsService.fetchUnassignedMails({
          status: tab,
          search,
          limit: PAGE_SIZE,
          offset,
        });
        if (generation !== listQueryRef.current) return;

        const results = Array.isArray(response?.results) ? response.results : [];
        setMails((previous) => (append ? [...previous, ...results] : results));
        nextOffsetRef.current = response?.next_offset ?? null;
        setHasMore(response?.next_offset != null);
        publishPendingCount(response?.pending_count ?? 0);
      } catch (requestError) {
        if (generation !== listQueryRef.current) return;
        console.error(requestError);
        setListError(describeRequestError(requestError, "No se pudieron cargar los correos"));
      } finally {
        if (append) appendingRef.current = false;
        if (generation === listQueryRef.current) {
          setListLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [tab, search, publishPendingCount],
  );

  const loadMoreMails = () => {
    if (!hasMore || listLoading || appendingRef.current) return;
    loadMails({ append: true });
  };

  const handleListScroll = (event) => {
    const listElement = event.currentTarget;
    const distanceToBottom =
      listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight;
    if (distanceToBottom <= LOAD_MORE_THRESHOLD_PX) loadMoreMails();
  };

  useEffect(() => {
    loadMails();
  }, [loadMails]);

  useEffect(() => {
    if (listLoading) return;
    if (mails.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!mails.some((mail) => mail.id === selectedId)) {
      setSelectedId(mails[0].id);
    }
  }, [mails, listLoading, selectedId]);

  useEffect(() => {
    setSelectedLead(null);
    setActionError(null);
    setConfirmAction(null);

    if (selectedId == null) {
      detailQueryRef.current += 1;
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return undefined;
    }

    const generation = ++detailQueryRef.current;
    setDetailLoading(true);
    setDetailError(null);

    mailsService
      .fetchUnassignedMail(selectedId)
      .then((data) => {
        if (generation !== detailQueryRef.current) return;
        setDetail(data);
      })
      .catch((requestError) => {
        if (generation !== detailQueryRef.current) return;
        console.error(requestError);
        setDetail(null);
        setDetailError(
          describeRequestError(requestError, "No se pudo cargar el correo", { notFound: MAIL_GONE }),
        );
      })
      .finally(() => {
        if (generation === detailQueryRef.current) setDetailLoading(false);
      });

    return undefined;
  }, [selectedId]);

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );

  const showToast = (nextToast) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(nextToast);
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  };

  const switchTab = (nextTab) => {
    if (nextTab === tab) return;
    setTab(nextTab);
    setMails([]);
    setHasMore(false);
    setListLoading(true);
    setSelectedId(null);
    setDetail(null);
  };

  const retryDetail = () => {
    const current = selectedId;
    setSelectedId(null);
    setTimeout(() => setSelectedId(current), 0);
  };

  const ensureLeadsLoaded = useCallback(async () => {
    if (leads !== null || leadsLoading) return;

    setLeadsLoading(true);
    setLeadsError(null);
    try {
      const data = await leadsService.myLeads();
      setLeads(Array.isArray(data) ? data.filter((lead) => lead.visible !== false) : []);
    } catch (requestError) {
      console.error(requestError);
      setLeadsError(describeRequestError(requestError, "No se pudieron cargar tus leads"));
    } finally {
      setLeadsLoading(false);
    }
  }, [leads, leadsLoading]);

  const removeMailFromList = (mailId, actionListContext) => {
    if (actionListContext !== listContextRef.current) return;

    const currentMails = mailsRef.current;
    const index = currentMails.findIndex((mail) => mail.id === mailId);
    if (index === -1) return;

    const remaining = currentMails.filter((mail) => mail.id !== mailId);
    if (nextOffsetRef.current != null) {
      nextOffsetRef.current = Math.max(0, nextOffsetRef.current - 1);
    }

    setMails((current) => current.filter((mail) => mail.id !== mailId));
    if (selectedIdRef.current === mailId) {
      const fallback = remaining[index] ?? remaining[index - 1] ?? null;
      setSelectedId(fallback ? fallback.id : null);
      if (!fallback) setDetail(null);
    }

    if (remaining.length === 0 && nextOffsetRef.current != null && !appendingRef.current) {
      loadMails({ append: true });
    }
  };

  const runAction = async (actionName, request, onSuccess) => {
    if (!detail || actionPending) return;

    const actionListContext = listContextRef.current;
    setActionPending(actionName);
    setActionError(null);
    try {
      const response = await request();
      onSuccess(response, actionListContext);
    } catch (requestError) {
      console.error(requestError);
      setActionError(
        describeRequestError(requestError, ACTION_FAILURES[actionName], { notFound: MAIL_GONE }),
      );
    } finally {
      setActionPending(null);
    }
  };

  const handleIgnore = () =>
    runAction(
      "ignore",
      () => mailsService.ignoreUnassignedMail(detail.id),
      (_, actionListContext) => {
        removeMailFromList(detail.id, actionListContext);
        publishPendingCount(Math.max(0, pendingCount - 1));
        showToast({
          message: "Correo ignorado. Lo encontrás en la pestaña Ignorados.",
        });
      },
    );

  const handleRestore = () =>
    runAction(
      "restore",
      () => mailsService.restoreUnassignedMail(detail.id),
      (_, actionListContext) => {
        removeMailFromList(detail.id, actionListContext);
        publishPendingCount(pendingCount + 1);
        showToast({ message: "Correo devuelto a Pendientes." });
      },
    );

  const handleSpam = () => {
    setConfirmAction(null);
    return runAction(
      "spam",
      () => mailsService.markUnassignedMailAsSpam(detail.id),
      (response, actionListContext) => {
        const reportDetail = response?.spam_report?.detail;
        removeMailFromList(detail.id, actionListContext);
        publishPendingCount(Math.max(0, pendingCount - 1));
        showToast({
          message: `Remitente ${detail.from_email} bloqueado.${reportDetail ? ` ${reportDetail}` : ""}`,
        });
      },
    );
  };

  const handleDelete = () => {
    setConfirmAction(null);
    return runAction(
      "delete",
      () => mailsService.deleteUnassignedMail(detail.id),
      (_, actionListContext) => {
        removeMailFromList(detail.id, actionListContext);
        showToast({ message: "Correo eliminado definitivamente." });
      },
    );
  };

  const handleContactCreatedForMail = (mailId, email, person) => {
    setDetail((previous) => {
      if (!previous || previous.id !== mailId) return previous;
      return {
        ...previous,
        persons: (previous.persons ?? []).map((entry) =>
          entry.email === email ? { ...entry, person } : entry,
        ),
      };
    });
  };

  const handleAssign = ({ lead, personIds, personNames }) =>
    runAction(
      "assign",
      () =>
        mailsService.assignUnassignedMail(detail.id, {
          leadId: lead.id,
          personIds,
        }),
      (_, actionListContext) => {
        removeMailFromList(detail.id, actionListContext);
        publishPendingCount(Math.max(0, pendingCount - 1));
        const relatedSentence =
          personNames.length > 0
            ? ` ${joinNames(personNames)} ${personNames.length === 1 ? "quedó" : "quedaron"} como ${personNames.length === 1 ? "persona relacionada" : "personas relacionadas"}.`
            : "";
        showToast({
          message: `Correo vinculado a ${lead.lead_name}.${relatedSentence}`,
          leadId: lead.id,
        });
      },
    );

  const handleLeadCreated = (response) => {
    const created = response?.data?.data ?? response?.data ?? response;
    if (!created?.id) return;

    setSelectedLead(created);
    setLeads((previous) => (previous ? [created, ...previous] : previous));
  };

  const openLead = (leadId) => navigate(`/leads/${leadId}`);

  const showAssignPanel = detail?.status === "pending" && !detailLoading;

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.content}>
          <div className={styles.pageTitle}>Sin asignar</div>

          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <SearchInput
                onSearch={setSearch}
                placeholder="Buscar remitente o asunto..."
                width={280}
                height={40}
              />
              <div className={styles.tabPill}>
                {TABS.map((tabOption) => (
                  <ChoiceChip
                    key={tabOption.value}
                    label={
                      tabOption.value === "pending"
                        ? `Pendientes · ${pendingCount}`
                        : tabOption.label
                    }
                    selected={tab === tabOption.value}
                    onClick={() => switchTab(tabOption.value)}
                    height={32}
                    iconSize={0}
                    fontWeight={500}
                    selectedColor="var(--text-cta-primary)"
                    unselectedColor="var(--text-tertiary)"
                    selectedBackground="var(--surface-accent-hover)"
                    unselectedBackground="transparent"
                  />
                ))}
              </div>
            </div>
            <span className={styles.hint}>
              Correos recibidos en tus casillas que no pudimos vincular a ningún lead
            </span>
          </div>

          <div
            className={
              showAssignPanel
                ? styles.workspace
                : `${styles.workspace} ${styles.workspaceTwoColumns}`
            }
          >
            <aside
              className={styles.listColumn}
              onScroll={handleListScroll}
              data-testid="unassigned-list-column"
            >
              <UnassignedMailList
                mails={mails}
                status={tab}
                search={search}
                selectedId={selectedId}
                loading={listLoading || (loadingMore && mails.length === 0)}
                loadingMore={loadingMore}
                hasMore={hasMore}
                error={listError}
                onSelect={(mail) => setSelectedId(mail.id)}
                onRetry={() => loadMails()}
                onLoadMore={loadMoreMails}
              />
            </aside>

            <section className={styles.detailColumn}>
              <UnassignedMailDetail
                mail={detail}
                loading={detailLoading}
                error={detailError}
                actionPending={actionPending}
                actionError={actionError}
                onIgnore={handleIgnore}
                onSpam={() => setConfirmAction("spam")}
                onDelete={() => setConfirmAction("delete")}
                onRestore={handleRestore}
                onOpenLead={openLead}
                onRetry={retryDetail}
              />
            </section>

            {showAssignPanel && (
              <aside className={styles.assignColumn}>
                <UnassignedMailAssignPanel
                  key={detail.id}
                  mail={detail}
                  leads={leads ?? []}
                  leadsLoading={leadsLoading}
                  leadsError={leadsError}
                  onLoadLeads={ensureLeadsLoaded}
                  selectedLead={selectedLead}
                  onSelectLead={setSelectedLead}
                  onCreateLead={() =>
                    setCreateLeadPrefill(
                      buildLeadPrefill(detail, { responsibleOptions: responsible, accounts }),
                    )
                  }
                  onContactCreated={handleContactCreatedForMail}
                  onAssign={handleAssign}
                  assigning={actionPending === "assign"}
                  assignError={actionError}
                />
              </aside>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <FloatingModal bottom={56} gap={16}>
          <div className={styles.toast} role="status">
            <span className={styles.toastMessage}>{toast.message}</span>
            {toast.leadId && (
              <button
                type="button"
                className={styles.toastLink}
                onClick={() => openLead(toast.leadId)}
              >
                Abrir lead →
              </button>
            )}
            <button
              type="button"
              className={styles.toastClose}
              onClick={() => setToast(null)}
              aria-label="Cerrar aviso"
            >
              ×
            </button>
          </div>
        </FloatingModal>
      )}

      {confirmAction === "spam" && detail && (
        <ConfirmDeleteModal
          title="¿Marcar como spam?"
          description="Bloqueamos al remitente en la casilla y le pedimos al servidor de correo que lo trate como spam. No vas a volver a recibir correos de esta dirección."
          name={detail.from_email}
          confirmLabel="Marcar como spam"
          confirmingLabel="Marcando..."
          onConfirm={handleSpam}
          onClose={() => setConfirmAction(null)}
        />
      )}

      {confirmAction === "delete" && detail && (
        <ConfirmDeleteModal
          title="¿Eliminar este correo?"
          description="Se borra definitivamente de MaqMailing y no tiene vuelta atrás."
          name={getSenderDisplayName(detail)}
          onConfirm={handleDelete}
          onClose={() => setConfirmAction(null)}
        />
      )}

      {createLeadPrefill && (
        <LeadInfoModal
          visible
          mode="create"
          lead={createLeadPrefill}
          statuses={activeStatuses}
          onClose={() => setCreateLeadPrefill(null)}
          onSaved={handleLeadCreated}
        />
      )}
    </div>
  );
};

export default UnassignedMailsPage;
